import { useTranslation } from 'react-i18next';
import type { AsyncState } from '@/hooks';
import type { AcademicRank } from '@/types/organisation';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Icon } from '@/components/icons';
import styles from '../AdminOrganisationPage.module.css';

const NS = 'admin.organisation';

/**
 * The Ranks tab — the seniority ladder, drawn as the statute draws it.
 *
 * Two facts about the Cameroonian rank table shape this view and must not be
 * "corrected" by a well-meaning refactor:
 *
 *   RESEARCH_FELLOW is a PARALLEL RESEARCH CORPS, not a rung. Drawing it inside the
 *   ladder would present a research posting as a promotion step between teaching
 *   ranks, which it is not — so it renders in its own section, visually apart.
 *
 *   SENIOR_LECTURER and LECTURER legitimately share the French name "Chargé de
 *   Cours" (the Cameroonian statute names them identically). Two rows with the same
 *   French label are correct data, not a bug; the EN name and canonical code keep
 *   the rows tellable-apart.
 *
 * And the rule the whole model rests on, said where ranks are shown: a rank confers
 * NO permissions — the identity database refuses a security role named after one.
 */

const PARALLEL_CORPS_CODES = new Set(['RESEARCH_FELLOW']);

export function RanksTab({
  state,
  onRetry,
  locale,
}: {
  state: AsyncState<AcademicRank[]>;
  onRetry: () => void;
  locale: string;
}) {
  const { t } = useTranslation();

  return (
    <section className={styles.section}>
      <Card>
        <p className={styles.callout}>
          <Icon name="info" size={16} />
          <span>{t(`${NS}.ranks.noPermissions`)}</span>
        </p>
        <AsyncPanel
          state={state}
          onRetry={onRetry}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t(`${NS}.ranks.empty`)}
          emptyBody={t(`${NS}.ranks.emptyBody`)}
        >
          {(data) => {
            const ladder = data.filter((rank) => !PARALLEL_CORPS_CODES.has(rank.code));
            const parallel = data.filter((rank) => PARALLEL_CORPS_CODES.has(rank.code));
            return (
              <>
                <ol className={styles.rankList}>
                  {ladder.map((rank) => (
                    <RankRow key={rank.id} rank={rank} locale={locale} />
                  ))}
                </ol>
                {parallel.length > 0 && (
                  <div className={styles.parallelCorps}>
                    <h3 className={styles.parallelCorpsTitle}>{t(`${NS}.ranks.parallelTitle`)}</h3>
                    <p className={styles.parallelCorpsBody}>{t(`${NS}.ranks.parallelBody`)}</p>
                    <ul className={styles.rankList}>
                      {parallel.map((rank) => (
                        <RankRow key={rank.id} rank={rank} locale={locale} parallel />
                      ))}
                    </ul>
                  </div>
                )}
              </>
            );
          }}
        </AsyncPanel>
      </Card>
    </section>
  );
}

function RankRow({ rank, locale, parallel = false }: { rank: AcademicRank; locale: string; parallel?: boolean }) {
  const { t } = useTranslation();
  // Both names are always shown — this is a bilingual statute, and the reader
  // conferring a rank needs the wording the decree will carry in either language.
  const primary = locale === 'fr' && rank.displayNameFr ? rank.displayNameFr : rank.displayName;
  const secondary = locale === 'fr' ? (rank.displayNameFr ? rank.displayName : null) : rank.displayNameFr;

  return (
    <li className={styles.rankRow}>
      <span className={parallel ? styles.rankOrderParallel : styles.rankOrder} aria-hidden="true">
        {parallel ? '∥' : rank.rankOrder}
      </span>
      <div className={styles.rankMain}>
        <span className={styles.rankName}>{primary}</span>
        {secondary && <span className={styles.rankAliases}>{secondary}</span>}
        <code className={styles.canonicalCode}>{rank.code}</code>
        {!rank.active && <Badge tone="neutral">{t(`${NS}.positions.inactive`)}</Badge>}
        {rank.aliasCodes.length > 0 && (
          <span className={styles.rankAliases}>
            {t(`${NS}.ranks.alsoKnownAs`, { codes: rank.aliasCodes.join(', ') })}
          </span>
        )}
      </div>
    </li>
  );
}
